import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    Alert
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, retryFn } from "@lib/apis";
import { useEffect, useState } from "react";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { urls } from "@routes";
import { SurveyAnalytics } from "@admin/types/survey.types";
import IconButton from "@mui/material/IconButton";

export default function AdminSurveyAnalytics() {
    const navigate = useNavigate();
    const { surveyUid } = useParams<{ surveyUid: string }>();

    const [analyticsData, setAnalyticsData] = useState<SurveyAnalytics>();

    // Query for fetching analytics
    const analyticsQuery = useQuery({
        queryKey: ["surveyAnalytics", surveyUid],
        queryFn: () => authenticateAndFetchData(`/admin/survey/analytics/?survey_uid=${surveyUid}`),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
        enabled: !!surveyUid,
    });

    useEffect(() => {
        if (analyticsQuery.data) {
            setAnalyticsData(analyticsQuery.data.data);
        }
    }, [analyticsQuery.data]);

    // Calculate percentage for distribution
    function calculatePercentage(count: number, total: number): number {
        if (total === 0) return 0;
        return (count / total) * 100;
    }

    // Render analytics for a specific question
    function renderQuestionAnalytics(questionAnalytics: SurveyAnalytics['question_analytics'][0]) {
        const { question_type, total_responses, distribution, average, sample_responses } = questionAnalytics;

        // For dropdown, radio, checkbox - show distribution
        if (["dropdown", "radio", "checkbox"].includes(question_type) && distribution) {
            const sortedDistribution = Object.entries(distribution).sort((a, b) => b[1] - a[1]);

            return (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Option</strong></TableCell>
                                <TableCell align="right"><strong>Count</strong></TableCell>
                                <TableCell align="right"><strong>Percentage</strong></TableCell>
                                <TableCell width="40%"><strong>Visual</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedDistribution.map(([option, count]) => {
                                const percentage = calculatePercentage(count, total_responses);
                                return (
                                    <TableRow key={option}>
                                        <TableCell>{option}</TableCell>
                                        <TableCell align="right">{count}</TableCell>
                                        <TableCell align="right">{percentage.toFixed(1)}%</TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={percentage}
                                                    sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                    {percentage.toFixed(0)}%
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }

        // For scale - show average and distribution
        if (question_type === "scale" && distribution) {
            const sortedDistribution = Object.entries(distribution)
                .map(([key, value]) => [parseInt(key), value] as [number, number])
                .sort((a, b) => a[0] - b[0]);

            return (
                <Stack spacing={2}>
                    {average !== undefined && (
                        <Box>
                            <Typography variant="h6" color="primary">
                                Average Rating: {average.toFixed(2)}
                            </Typography>
                        </Box>
                    )}
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Rating</strong></TableCell>
                                    <TableCell align="right"><strong>Count</strong></TableCell>
                                    <TableCell align="right"><strong>Percentage</strong></TableCell>
                                    <TableCell width="40%"><strong>Visual</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedDistribution.map(([rating, count]) => {
                                    const percentage = calculatePercentage(count, total_responses);
                                    return (
                                        <TableRow key={rating}>
                                            <TableCell>{rating}</TableCell>
                                            <TableCell align="right">{count}</TableCell>
                                            <TableCell align="right">{percentage.toFixed(1)}%</TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={percentage}
                                                        sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {percentage.toFixed(0)}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>
            );
        }

        // For text responses - show samples
        if (["text", "textarea"].includes(question_type) && sample_responses) {
            return (
                <Stack spacing={1}>
                    {sample_responses.length > 0 ? (
                        <>
                            <Typography variant="body2" color="text.secondary">
                                Sample responses (showing up to 10):
                            </Typography>
                            {sample_responses.map((response, index) => (
                                <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="body2">{response}</Typography>
                                </Paper>
                            ))}
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No responses yet
                        </Typography>
                    )}
                </Stack>
            );
        }

        return (
            <Typography variant="body2" color="text.secondary">
                No analytics data available
            </Typography>
        );
    }

    if (analyticsQuery.isLoading) {
        return <PageLoading />;
    }

    if (analyticsQuery.isError) {
        return <PageDataErrorHandler error={analyticsQuery.error as ApiRequestFailed} />;
    }

    if (!analyticsData) {
        return null;
    }

    return (
        <Stack direction="column" spacing={3} sx={{ pb: 4 }}>
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography fontWeight="bold" variant="h5">
                        Survey Analytics
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 0.5 }}>
                        {analyticsData.survey_title}
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(urls["adminSurveys"])}
                >
                    Back to Surveys
                </Button>
            </Stack>

            <Divider />

            {/* Overall Survey Stats */}
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Card sx={{ flexGrow: 1, minWidth: "200px" }}>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary">
                            Total Completions
                        </Typography>
                        <Typography variant="h4" sx={{ mt: 1 }}>
                            {analyticsData.total_completions}
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{ flexGrow: 1, minWidth: "200px" }}>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary">
                            Reward Credits
                        </Typography>
                        <Typography variant="h4" sx={{ mt: 1 }} color="warning.main">
                            {analyticsData.reward_credits}
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{ flexGrow: 1, minWidth: "200px" }}>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary">
                            Reward Domain Credits
                        </Typography>
                        <Typography variant="h4" sx={{ mt: 1 }} color="warning.main">
                            {analyticsData.domain_credits}
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{ flexGrow: 1, minWidth: "200px" }}>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary">
                            Questions
                        </Typography>
                        <Typography variant="h4" sx={{ mt: 1 }}>
                            {analyticsData.question_count}
                        </Typography>
                    </CardContent>
                </Card>
            </Stack>

            {/* Credits Stats */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Credits Statistics
                    </Typography>
                    <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Users Email Awarded
                            </Typography>
                            <Typography variant="h5" color="success.main">
                                {analyticsData.credits_stats.users_awarded}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Total Credits Given
                            </Typography>
                            <Typography variant="h5" color="warning.main">
                                {analyticsData.credits_stats.total_credits_given}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Users Domain Awarded
                            </Typography>
                            <Typography variant="h5" color="success.main">
                                {analyticsData.domain_stats.users_awarded}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Total Domain Credits Given
                            </Typography>
                            <Typography variant="h5" color="warning.main">
                                {analyticsData.domain_stats.total_domains_given}
                            </Typography>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            {/* Question-by-Question Analytics */}
            <Typography variant="h6" fontWeight="bold">
                Question Analytics
            </Typography>

            {analyticsData.question_analytics && analyticsData.question_analytics.length > 0 ? (
                <Stack spacing={3}>
                    {analyticsData.question_analytics.map((questionAnalytics, index) => (
                        <Card key={questionAnalytics.question_uid}>
                            <CardContent>
                                <Stack spacing={2}>
                                    <Box>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                Question {index + 1}
                                            </Typography>
                                            <Chip
                                                label={questionAnalytics.question_type}
                                                size="small"
                                                color="primary"
                                            />
                                            <Chip
                                                label={`${questionAnalytics.total_responses} responses`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Stack>
                                        <Typography variant="body1" sx={{ mt: 1 }}>
                                            {questionAnalytics.question_text}
                                        </Typography>
                                    </Box>

                                    <Divider />

                                    {renderQuestionAnalytics(questionAnalytics)}
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            ) : (
                <Alert severity="info">
                    No question analytics available yet. Users need to complete this survey first.
                </Alert>
            )}
        </Stack>
    );
}
